import { api } from './api';
import { parcelMapQuery } from '../utils';

/** What a person edits on the investor screen; the rest of its row is not theirs to change. */
const COLUMNS = ['NAME', 'EMAIL', 'PHONE', 'CELL', 'ADDRESS', 'CITY', 'STATE', 'ZIP', 'FIRM_ID', 'DECEASED', 'SECOND_CONTACT_ID', 'ACTIVE_BUYER'];

/** The investor lookup's territory filters, named as the parcel map names the list's. */
const TERRITORY_KEYS = ['territory_agent', 'territory', 'territory_market'];

/**
 * Save an edit from the investor screen. The server carries it on to the
 * investor's Kore lead, and refuses the save when Kore does not accept it.
 *
 * @param {string|number} id ARCIMEDES_ROW_ID
 * @param {Object} investor the row as the screen holds it
 * @returns {Promise<Object>} the saved investor
 */
export function updateInvestor(id, investor) {
    const body = {};

    COLUMNS.forEach((column) => {
        if (investor[column] !== undefined) body[column] = investor[column];
    });

    return api.patch(`/investors/${id}`, body).then((res) => res.data);
}

/**
 * The territory filters of a query that already uses the lookup's names, each
 * a comma list of ids; null when there are none.
 *
 * @param {Object} query
 * @returns {Object|null}
 */
export function territoryFilter(query) {
    const territory = {};

    TERRITORY_KEYS.forEach((key) => {
        const value = query && query[key];
        if (value && /^\d+(,\d+)*$/.test(String(value))) territory[key] = String(value);
    });

    return Object.keys(territory).length ? territory : null;
}

/** The territory a property-list URL is narrowed to (agent, territories, markets), or null. */
export function listTerritoryFilter(listQuery) {
    return territoryFilter(parcelMapQuery(listQuery));
}

/**
 * One page of the investors ticked Active Buyer in Kore who own a property in
 * the territory now.
 *
 * @param {Object} territory what territoryFilter() returned
 * @returns {Promise<{data: Object[], meta: Object}>}
 */
export function fetchActiveBuyers(territory, page, perPage) {
    const query = { active_buyer: 1, page, per_page: perPage };

    Object.keys(territory).forEach((key) => {
        query[key] = territory[key].split(',');
    });

    return api.get('/map/investors', query);
}

/**
 * The Active Buyers tile of a territory's stats strip: the count and the
 * Investors page it opens. Null when the list has no territory; a failed count
 * comes back as `error`.
 *
 * @param {Object} listQuery the property list's URL query
 * @returns {Promise<{count: number, link: string}|{error: string}|null>}
 */
export function fetchActiveBuyersTile(listQuery) {
    const territory = listTerritoryFilter(listQuery);
    if (!territory) return Promise.resolve(null);

    const link = '/investors?ACTIVE_BUYERS=y' + Object.keys(territory)
        .map((key) => '&' + key + '=' + encodeURIComponent(territory[key]))
        .join('');

    return fetchActiveBuyers(territory, 1, 1)
        .then((envelope) => ({ count: envelope.meta.pagination.total, link }))
        .catch((error) => ({ error: 'Couldn’t count the active buyers: ' + error.message }));
}
